import { useEffect, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  fetchAdminUkrposhtaShipmentSummary,
  type AdminOrderSummary,
  type AdminUkrposhtaShipmentSummary,
} from '@/services/adminService';
import { logging } from '@/services/logging';
import type { AdminWorkspaceCurrencyCode } from '@/components/admin/ordersWorkspace/types';
import { formatCurrencyAmount } from '@/components/admin/ordersWorkspace/utils';
import axios from 'axios';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
};

const formatShipmentCurrency = (
  amount: number | null,
  sourceCurrencyCode: string | null,
  currencyCode: AdminWorkspaceCurrencyCode,
  usdToUahRate: number | null,
): string => {
  if (amount == null || !sourceCurrencyCode) {
    return '—';
  }

  return formatCurrencyAmount(amount, sourceCurrencyCode, currencyCode, usdToUahRate);
};

const formatSizeInches = (width: number | null, height: number | null): string => (
  width != null && height != null ? String(width) + ' x ' + String(height) + ' in' : '—'
);

const formatDiscountLevel = (level: AdminUkrposhtaShipmentSummary['userDiscountLevel']): string => (
  level ? level.type + ' (' + String(level.percent) + '%)' : '—'
);

const formatHistoricalUsdAmount = (
  amountUsd: number | null,
  currencyCode: AdminWorkspaceCurrencyCode,
  confirmedUsdToUahRate: number | null,
): string => {
  if (amountUsd == null || !Number.isFinite(amountUsd)) {
    return '—';
  }

  if (currencyCode === 'USD') {
    return formatCurrencyAmount(amountUsd, 'USD', 'USD', null);
  }

  if (confirmedUsdToUahRate == null || !Number.isFinite(confirmedUsdToUahRate) || confirmedUsdToUahRate <= 0) {
    return '—';
  }

  return formatCurrencyAmount(amountUsd * confirmedUsdToUahRate, 'UAH', 'UAH', null);
};

const formatOriginalCurrencyAmount = (
  amount: number | null,
  currencyCode: string | null,
): string => {
  if (amount == null || !currencyCode) {
    return '—';
  }

  if (currencyCode === 'USD' || currencyCode === 'UAH') {
    return formatCurrencyAmount(amount, currencyCode, currencyCode, null);
  }

  return `${currencyCode} ${amount}`;
};

const ShipmentBarcode = ({ value }: { value: string | null }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!svgRef.current || !value || value.trim().length === 0) {
      return;
    }

    JsBarcode(svgRef.current, value, {
      format: 'CODE128',
      displayValue: false,
      margin: 0,
      height: 44,
      width: 1.4,
      background: '#ffffff',
      lineColor: '#111827',
    });
  }, [value]);

  if (!value || value.trim().length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="rounded-md border bg-white p-2">
      <svg ref={svgRef} aria-label={`Barcode ${value}`} className="h-12 w-full" />
    </div>
  );
};

interface UkrposhtaShipmentSummaryDialogProps {
  readonly open: boolean;
  readonly order: AdminOrderSummary | null;
  readonly onClose: () => void;
  readonly currencyCode?: AdminWorkspaceCurrencyCode;
  readonly usdToUahRate?: number | null;
}

export const UkrposhtaShipmentSummaryDialog = ({
  open,
  order,
  onClose,
  currencyCode = 'USD',
  usdToUahRate = null,
}: UkrposhtaShipmentSummaryDialogProps) => {
  const [shipment, setShipment] = useState<AdminUkrposhtaShipmentSummary | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !order) {
      return;
    }

    let cancelled = false;
    setShipment(null);
    setStatus('loading');
    setError(null);

    void fetchAdminUkrposhtaShipmentSummary(order.id)
      .then((payload) => {
        if (cancelled) {
          return;
        }
        setShipment(payload);
        setStatus('idle');
      })
      .catch((fetchError) => {
        if (cancelled) {
          return;
        }
        logging.error('Failed to load Ukrposhta shipment summary', { error: fetchError, orderId: order.id });
        setError(getApiErrorMessage(fetchError, 'Unable to load the Ukrposhta shipment summary.'));
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [open, order]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    }}>
      <DialogContent className="max-w-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>{shipment?.fulfillmentMode === 'PICKUP' ? 'Fulfillment summary' : 'Shipment summary'}</DialogTitle>
          <DialogDescription>
            {shipment?.fulfillmentMode === 'PICKUP'
              ? 'Pickup order details for internal handling.'
              : 'Printable order and shipment details for Ukrposhta, even if the shipment has not been created yet.'}
          </DialogDescription>
        </DialogHeader>
        {status === 'loading' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading shipment details...
          </div>
        )}
        {status === 'error' && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error ?? 'Unable to load the Ukrposhta shipment summary.'}
          </div>
        )}
        {shipment && (
          <div className="max-h-[80vh] overflow-y-auto pr-2">
            <div className="grid gap-6 pb-1 text-sm">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-md border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Shipment</div>
                <div className="mt-2 space-y-1">
                  <p><span className="font-medium">Order ID:</span> <span className="break-all font-mono text-xs">{shipment.displayCode}</span></p>
                  <p><span className="font-medium">Shipment UUID:</span> <span className="break-all font-mono text-xs">{shipment.shipmentUuid ?? '—'}</span></p>
                  <p><span className="font-medium">Barcode:</span> <span className="break-all font-mono text-xs">{shipment.barcode ?? '—'}</span></p>
                  <ShipmentBarcode value={shipment.barcode} />
                  <p><span className="font-medium">Fulfillment:</span> {shipment.fulfillmentMode === 'PICKUP' ? 'Self pickup' : 'Shipping'}</p>
                  <p><span className="font-medium">Status:</span> {shipment.shipmentStatus ?? shipment.orderStatus}</p>
                  <p><span className="font-medium">Option:</span> {shipment.shippingOption ?? '—'}</p>
                  <p><span className="font-medium">Wallet top-up original sum:</span> {formatOriginalCurrencyAmount(shipment.walletTopUpOriginalAmount, shipment.walletTopUpOriginalCurrency)}</p>
                  <p><span className="font-medium">Total order cost:</span> {formatHistoricalUsdAmount(shipment.totalOrderCostAmount, currencyCode, shipment.confirmedUsdToUahRate)}</p>
                  <p><span className="font-medium">{shipment.fulfillmentMode === 'PICKUP' ? 'Items cost:' : 'Cost of printed items:'}</span> {formatShipmentCurrency(shipment.printedItemsCostUsd, 'USD', currencyCode, usdToUahRate)}</p>
                  <p><span className="font-medium">Quoted shipping:</span> {shipment.shippingOriginalAmount != null && shipment.shippingOriginalCurrency ? formatShipmentCurrency(shipment.shippingOriginalAmount, shipment.shippingOriginalCurrency, currencyCode, usdToUahRate) : shipment.shippingAmountUsd != null ? formatHistoricalUsdAmount(shipment.shippingAmountUsd, currencyCode, shipment.confirmedUsdToUahRate) : '—'}</p>
                  <p><span className="font-medium">User discount level:</span> {formatDiscountLevel(shipment.userDiscountLevel)}</p>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{shipment.fulfillmentMode === 'PICKUP' ? 'Fulfillment profile' : 'Shipment profile'}</div>
                <div className="mt-2 space-y-1">
                  <p><span className="font-medium">Package type:</span> {shipment.shipmentProfile.packageType ?? '—'}</p>
                  {shipment.fulfillmentMode === 'PICKUP' ? (
                    <p><span className="font-medium">Pickup:</span> Customer collects the order without delivery.</p>
                  ) : null}
                  <p><span className="font-medium">Transport:</span> {shipment.shipmentProfile.transportType ?? '—'}</p>
                  <p><span className="font-medium">Delivery type:</span> {shipment.shipmentProfile.deliveryType ?? '—'}</p>
                  <p><span className="font-medium">Weight:</span> {shipment.shipmentProfile.weight != null ? `${shipment.shipmentProfile.weight} g` : '—'}</p>
                  <p><span className="font-medium">Size:</span> {shipment.shipmentProfile.length != null && shipment.shipmentProfile.width != null && shipment.shipmentProfile.height != null ? `${shipment.shipmentProfile.length} x ${shipment.shipmentProfile.width} x ${shipment.shipmentProfile.height} cm` : '—'}</p>
                  <p><span className="font-medium">Actual shipping:</span> {formatShipmentCurrency(shipment.shippingActualAmount, shipment.shippingActualCurrency, currencyCode, usdToUahRate)}</p>
                  <p><span className="font-medium">Packaging type:</span> {shipment.packagingType ?? '—'}</p>
                  <p><span className="font-medium">Packaging SKU:</span> {shipment.packagingSku ?? shipment.packaging ?? '—'}</p>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recipient</div>
                <div className="mt-2 space-y-1">
                  <p><span className="font-medium">Name:</span> {shipment.recipient ? `${shipment.recipient.firstName} ${shipment.recipient.lastName}`.trim() : '—'}</p>
                  <p><span className="font-medium">Phone:</span> {shipment.recipient?.phone ?? '—'}</p>
                  <p><span className="font-medium">Email:</span> {shipment.customer?.email ?? '—'}</p>
                  <p><span className="font-medium">Country:</span> {shipment.recipient?.countryCode ?? '—'}</p>
                  <p><span className="font-medium">ZIP code:</span> {shipment.recipient?.postalCode ?? '—'}</p>
                  <p><span className="font-medium">Region:</span> {shipment.recipient?.region ?? '—'}</p>
                  <p><span className="font-medium">City:</span> {shipment.recipient?.city ?? '—'}</p>
                  <p><span className="font-medium">Street 1:</span> {shipment.recipient?.street1 ?? '—'}</p>
                  <p><span className="font-medium">Street 2:</span> {shipment.recipient?.street2 ?? '—'}</p>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sender</div>
                <div className="mt-2 space-y-1">
                  <p><span className="font-medium">Name:</span> {shipment.sender.name}</p>
                  <p><span className="font-medium">Phone:</span> {shipment.sender.phone ?? '—'}</p>
                  <p><span className="font-medium">ZIP code:</span> {shipment.sender.address.postcode}</p>
                  <p><span className="font-medium">Region:</span> {shipment.sender.address.region}</p>
                  <p><span className="font-medium">City:</span> {shipment.sender.address.city}</p>
                  <p><span className="font-medium">Street:</span> {shipment.sender.address.street}</p>
                  <p><span className="font-medium">House:</span> {shipment.sender.address.houseNumber ?? '—'}</p>
                </div>
              </div>
              <div className="rounded-md border p-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Parcel items</div>
                <div className="mt-2 space-y-3">
                  {shipment.parcelItems.length > 0 ? shipment.parcelItems.map((item, index) => (
                    <div key={`${item.latinName ?? item.description ?? 'item'}-${index}`} className="rounded border p-2">
                      <p><span className="font-medium">Item:</span> {item.itemName ?? item.latinName ?? item.description ?? '—'}</p>
                      <p><span className="font-medium">Qty:</span> {item.quantity ?? '—'}</p>
                      <p><span className="font-medium">Item cost:</span> {formatShipmentCurrency(item.priceUsd, 'USD', currencyCode, usdToUahRate)}</p>
                      <p><span className="font-medium">Total size:</span> {formatSizeInches(item.totalWidthIn, item.totalHeightIn)}</p>
                      <p><span className="font-medium">Declared value:</span> {item.value != null && item.currencyCode ? formatShipmentCurrency(item.value, item.currencyCode, currencyCode, usdToUahRate) : '—'}</p>
                      <p><span className="font-medium">Weight:</span> {item.weight != null ? String(item.weight) + ' g' : '—'}</p>
                      <p><span className="font-medium">Origin:</span> {item.countryOfOrigin ?? '—'}</p>
                      <p><span className="font-medium">HS code:</span> {item.hsCode ?? '—'}</p>
                    </div>
                  )) : (
                    <p className="text-muted-foreground">No parcel items saved.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="rounded-md border p-3 text-xs text-muted-foreground">
              Use this summary as a fallback printout for the post office when the Ukrposhta label PDF endpoint is temporarily unavailable.
            </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
